// Lightweight tiered asset preloader. Warms the browser's HTTP cache via
// fetch() so subsequent GLTFLoader / THREE.AudioLoader / <img> consumers
// hit local memory instead of the network.
//
// Why fetch is enough: Vite serves all /public assets with normal HTTP
// cache headers. A `fetch(url)` that doesn't read the body still completes
// the response and the browser stores it. The downstream loaders (e.g.
// SpaceshipAssets's GLTFLoader.load(), MenuAudio.preload's AudioLoader,
// HomeScreen's <img> tags) make their own HTTP requests, the browser
// short-circuits to the cache, and there's no need to share decoded
// buffers across this layer and the existing per-system caches.
//
// Tier semantics:
//   tier 1 — tutorial. The first thing a fresh player encounters, so the
//            tutorial's full asset set (env, materials, narration, music,
//            SFX) loads ahead of everything else. Awaited by
//            LanguageSelectScreen before advancing to splash.
//   tier 2 — splash-critical. The splash choreography (music cue at 1.0s,
//            logo slam at 2.25s) lands on already-cached assets.
//   tier 3 — home / menu / changing room / map picker. Loads while the
//            user is on splash + home; almost always done by the time the
//            user picks a menu item.
//   tier 4 — shared in-match SFX. Loads while the user navigates menus.
//   tier 5 — per-map heavy assets + uncurated bucket. Curated map-specific
//            GLBs / music / textures plus anything auto-appended by
//            ~/dev/scripts/asset-compact for compacted assets that didn't
//            already have a manifest entry.

import { ASSET_MANIFEST, type AssetEntry } from './manifest';
import { getNarrationKeys, localeHasNarration } from '../maps/bundles/tutorial/narration/subtitles';

type Tier = 1 | 2 | 3 | 4 | 5;

export interface PreloadProgress {
  totalAssets: number;
  completedAssets: number;
  bytesDownloaded: number;
  completedTiers: ReadonlySet<Tier>;
}

type ProgressListener = (p: PreloadProgress) => void;

class Preloader {
  /** Resolves once every entry in the given tier has either fetched or
   *  failed. We swallow individual failures (a missing asset shouldn't
   *  block the splash); they'll surface again later when the actual
   *  loader tries to use them. */
  private tierPromises: Record<Tier, Promise<void> | null> = { 1: null, 2: null, 3: null, 4: null, 5: null };
  private started = false;

  private completedAssets = 0;
  private bytesDownloaded = 0;
  private completedTiers: Set<Tier> = new Set();
  private listeners: Set<ProgressListener> = new Set();
  /** Locales whose tutorial narration we've already kicked off a warm for.
   *  Re-selecting the same flag must not re-fire the fetches. */
  private warmedLocaleNarration: Set<string> = new Set();

  getProgress(): PreloadProgress {
    return {
      totalAssets: ASSET_MANIFEST.length,
      completedAssets: this.completedAssets,
      bytesDownloaded: this.bytesDownloaded,
      completedTiers: this.completedTiers,
    };
  }

  subscribe(listener: ProgressListener): () => void {
    this.listeners.add(listener);
    listener(this.getProgress());
    return () => { this.listeners.delete(listener); };
  }

  private emit(): void {
    const p = this.getProgress();
    for (const l of this.listeners) l(p);
  }

  /** Kick off all five tiers. Tier 1 fires immediately, each subsequent
   *  tier waits for the previous — sequential at the tier level so we
   *  don't flood the connection with low-priority assets while the
   *  splash is still racing for music + logo. Within a tier everything
   *  fires in parallel.
   *
   *  Idempotent — second call is a no-op. */
  startAll(): void {
    if (this.started) return;
    this.started = true;
    this.tierPromises[1] = this.runTier(1);
    this.tierPromises[2] = this.tierPromises[1].then(() => this.runTier(2));
    this.tierPromises[3] = this.tierPromises[2].then(() => this.runTier(3));
    this.tierPromises[4] = this.tierPromises[3].then(() => this.runTier(4));
    this.tierPromises[5] = this.tierPromises[4].then(() => this.runTier(5));
  }

  /** Block on a specific tier completing. If startAll() hasn't been called
   *  yet, calling awaitTier auto-starts it (so callers don't need to
   *  remember the order). */
  awaitTier(tier: Tier): Promise<void> {
    if (!this.started) this.startAll();
    return this.tierPromises[tier]!;
  }

  private async runTier(tier: Tier): Promise<void> {
    const entries = ASSET_MANIFEST.filter((e) => e.tier === tier);
    await Promise.allSettled(entries.map((e) => this.warm(e)));
    this.completedTiers.add(tier);
    this.emit();
  }

  /** Warm every tutorial-narration .ogg for the given locale. English is
   *  already in the static manifest (tier 1) — this method targets every
   *  OTHER locale, fired the moment the user picks a flag in
   *  LanguageSelectScreen so the audio is in cache by the time the
   *  tutorial starts.
   *
   *  No-ops when the locale doesn't ship narration (the NarratorEngine
   *  falls back to English at play time anyway, and English is already
   *  warm). Idempotent — second call for the same locale is a no-op. */
  preloadLocaleTutorialNarration(locale: string): void {
    if (locale === 'en') return;
    if (this.warmedLocaleNarration.has(locale)) return;
    if (!localeHasNarration(locale)) return;
    this.warmedLocaleNarration.add(locale);
    const keys = getNarrationKeys();
    // Fire-and-forget — these aren't tier-gated and shouldn't block the
    // splash. We don't fold them into completedAssets / bytesDownloaded
    // either (the progress UI is keyed to ASSET_MANIFEST.length).
    for (const key of keys) {
      const url = `/sounds/narration/tutorial/${locale}/${key}.ogg`;
      void fetch(url, { credentials: 'same-origin' })
        .then((res) => res.ok ? res.arrayBuffer() : null)
        .catch((err) => { console.warn(`[preloader] narration ${url} failed:`, err); });
    }
  }

  /** Fetch the URL and discard the body. The browser caches the response
   *  per its normal HTTP rules. Errors are logged once and swallowed —
   *  downstream loaders raise the real error in context if it matters. */
  private async warm(entry: AssetEntry): Promise<void> {
    try {
      const res = await fetch(entry.url, { credentials: 'same-origin' });
      if (!res.ok) {
        console.warn(`[preloader] ${entry.kind} ${entry.url} → ${res.status}`);
        return;
      }
      // Drain the body so the response moves through the network stack and
      // the browser commits it to cache. Discarded immediately afterwards.
      const buf = await res.arrayBuffer();
      this.bytesDownloaded += buf.byteLength;
    } catch (err) {
      console.warn(`[preloader] ${entry.kind} ${entry.url} failed:`, err);
    } finally {
      this.completedAssets += 1;
      this.emit();
    }
  }
}

export const preloader = new Preloader();
